import { faArrowUpArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../index';

export type SortDirection = 'asc' | 'desc';

export interface SortOption {
    field: string;
    direction: SortDirection;
    label: string;
}

export interface SortCallbackData {
    field: string;
    direction: SortDirection;
    label: string;
}

export interface SortProps {
    /**
     * Opciones de ordenamiento disponibles
     */
    options: SortOption[];
    /**
     * Callback cuando se selecciona una opción
     */
    onSort: (data: SortCallbackData) => void;
    /**
     * Etiqueta por defecto cuando no hay selección
     */
    defaultLabel?: string;
    /**
     * Valor inicial específico (para sincronización con URL)
     */
    initialValue?: { field: string; direction: SortDirection };
    /**
     * Variante del botón
     */
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'icon';
    /**
     * Tamaño del botón
     */
    size?: 'sm' | 'default' | 'lg';
    /**
     * Clases CSS adicionales
     */
    className?: string;
    /**
     * Si está deshabilitado
     */
    disabled?: boolean;
    /**
     * Test ID para testing
     */
    dataTestId?: string;
}

export const Sort = ({
    options,
    onSort,
    defaultLabel = 'Ordenar',
    initialValue,
    variant = 'outline',
    size = 'sm',
    className,
    disabled = false,
    dataTestId = 'sort'
}: SortProps) => {
    // 🎯 Función para encontrar el label inicial
    const getInitialLabel = () => {
        if (initialValue) {
            const foundOption = options.find(
                option => option.field === initialValue.field && option.direction === initialValue.direction
            );
            return foundOption?.label || defaultLabel;
        }
        return defaultLabel;
    };

    const [currentLabel, setCurrentLabel] = useState(getInitialLabel);
    const [isOpen, setIsOpen] = useState(false);

    // 🎯 Sincronizar label cuando cambie initialValue (desde URL)
    useEffect(() => {
        const newLabel = getInitialLabel();
        setCurrentLabel(newLabel);
    }, [initialValue, options]);

    const handleSelect = (option: SortOption) => {
        setCurrentLabel(option.label);
        setIsOpen(false);
        onSort({
            field: option.field,
            direction: option.direction,
            label: option.label
        });
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant={variant}
                    size={size}
                    icon={faArrowUpArrowDown}
                    classNameIcon="!h-4"
                    className={className + ' border-card-border'}
                    disabled={disabled}
                    data-testid={`${dataTestId}-trigger`}
                >
                    <span className="hidden sm:inline">{currentLabel}</span>
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="end"
                className="min-w-[140px]"
                data-testid={`${dataTestId}-content`}
            >
                {options.map((option, index) => (
                    <DropdownMenuItem
                        key={`${option.field}-${option.direction}-${index}`}
                        data-testid={`${dataTestId}-option-${option.field}-${option.direction}`}
                        onClick={() => handleSelect(option)}
                    >
                        {option.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}; 